import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { MarineOrganizationAccess } from './AuthGate';
import { useMarineAccess } from './AuthGate';
import { supabase } from './lib/supabase';
import './training.css';

type Course = {
  id: string;
  organization_id: string;
  course_code: string;
  title: string;
  description: string | null;
  edition: string | null;
  duration_weeks: number | null;
  status: string;
  source_file_name: string | null;
  source_storage_path: string | null;
  source_page_count: number | null;
  completion_note: string | null;
  sequential_required: boolean;
  created_at: string;
};

type Lesson = {
  id: string;
  organization_id: string;
  course_id: string;
  sort_order: number;
  module_number: number | null;
  section_type: 'module' | 'reference' | 'assessment';
  title: string;
  learning_objective: string | null;
  content_text: string | null;
  source_page_start: number | null;
  source_page_end: number | null;
  required: boolean;
  quiz_required: boolean;
  quiz_pass_percent: number;
  quiz_question_count: number;
};

type Completion = {
  lesson_id: string;
  completed_at: string;
  completion_method: 'legacy' | 'manual' | 'quiz' | 'admin_override';
  score_percent: number | null;
};

type TrainingLearner = {
  user_id: string;
  full_name: string;
  email: string;
  roles: string[];
};

type TrainingAssignment = {
  id: string;
  organization_id: string;
  course_id: string;
  user_id: string;
  assigned_at: string;
  due_at: string | null;
  cancelled_at: string | null;
};

type QuizQuestion = {
  id: string;
  prompt: string;
  options: string[];
  sort_order: number;
};

type QuizFeedback = {
  question_id: string;
  correct: boolean;
  selected_index: number;
  correct_index: number;
  critical: boolean;
  explanation: string | null;
};

type QuizResult = {
  passed: boolean;
  score_percent: number;
  pass_percent: number;
  correct_count: number;
  total_questions: number;
  critical_missed: number;
  feedback: QuizFeedback[];
  next_lesson: { id:string; module_number:number|null; title:string } | null;
  course_modules_complete: boolean;
};

export default function TrainingWorkspace({ organization }: { organization: MarineOrganizationAccess }) {
  const access = useMarineAccess();
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [learners, setLearners] = useState<TrainingLearner[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState(access.userId);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [manualSavingId, setManualSavingId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadingCourseId, setUploadingCourseId] = useState('');
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [quizLesson, setQuizLesson] = useState<Lesson | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizError, setQuizError] = useState('');
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState('');

  const canManage = access.isPlatformAdmin || organization.roles.some((role) => ['owner_admin', 'service_manager'].includes(role));

  async function loadLearnerProgress(userId: string, courseIds = courses.map((course) => course.id)) {
    if (!supabase) return;
    if (!userId || courseIds.length === 0) {
      setCompletions([]);
      setProgressLoading(false);
      return;
    }

    setProgressLoading(true);
    const completionResult = await supabase
      .from('marine_training_completions')
      .select('lesson_id,completed_at,completion_method,score_percent')
      .eq('organization_id', organization.id)
      .eq('user_id', userId)
      .in('course_id', courseIds);

    if (completionResult.error) {
      setError(completionResult.error.message);
      setCompletions([]);
    } else {
      setCompletions((completionResult.data || []) as Completion[]);
    }
    setProgressLoading(false);
  }

  async function loadAssignments() {
    if (!supabase) return;
    let query = supabase
      .from('marine_training_assignments')
      .select('id,organization_id,course_id,user_id,assigned_at,due_at,cancelled_at')
      .eq('organization_id', organization.id);
    if (!canManage) query = query.eq('user_id', access.userId);
    const result = await query;
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setAssignments((result.data || []) as TrainingAssignment[]);
  }

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError('');
    const { data: courseRows, error: courseError } = await supabase
      .from('marine_training_courses')
      .select('id,organization_id,course_code,title,description,edition,duration_weeks,status,source_file_name,source_storage_path,source_page_count,completion_note,sequential_required,created_at')
      .eq('organization_id', organization.id)
      .neq('status', 'archived')
      .order('created_at', { ascending: true });

    if (courseError) {
      setError(courseError.message);
      setLoading(false);
      return;
    }

    const nextCourses = (courseRows || []) as Course[];
    const ids = nextCourses.map((course) => course.id);
    let memberQuery = supabase
      .from('marine_organization_members')
      .select('user_id,role')
      .eq('organization_id', organization.id)
      .eq('active', true);
    let assignmentQuery = supabase
      .from('marine_training_assignments')
      .select('id,organization_id,course_id,user_id,assigned_at,due_at,cancelled_at')
      .eq('organization_id', organization.id);
    if (!canManage) {
      memberQuery = memberQuery.eq('user_id', access.userId);
      assignmentQuery = assignmentQuery.eq('user_id', access.userId);
    }

    const [
      { data: lessonRows, error: lessonError },
      { data: memberRows, error: memberError },
      { data: assignmentRows, error: assignmentError },
    ] = await Promise.all([
      ids.length
        ? supabase
            .from('marine_training_lessons')
            .select('id,organization_id,course_id,sort_order,module_number,section_type,title,learning_objective,content_text,source_page_start,source_page_end,required,quiz_required,quiz_pass_percent,quiz_question_count')
            .in('course_id', ids)
            .order('sort_order', { ascending: true })
        : Promise.resolve({ data: [], error: null }),
      memberQuery,
      assignmentQuery,
    ]);

    if (lessonError) {
      setError(lessonError.message);
      setLoading(false);
      return;
    }

    const nextLessons = (lessonRows || []) as Lesson[];
    const memberships = (memberRows || []) as Array<{ user_id: string; role: string }>;
    const learnerIds = [...new Set(memberships.map((member) => String(member.user_id)))];
    const profileResult = learnerIds.length
      ? await supabase
          .from('marine_profiles')
          .select('user_id,full_name,email')
          .in('user_id', learnerIds)
      : { data: [], error: null };

    const profileByUser = new Map(
      ((profileResult.data || []) as Array<{ user_id: string; full_name: string | null; email: string | null }>)
        .map((profile) => [String(profile.user_id), profile]),
    );
    const nextLearners = learnerIds.map((userId, index) => {
      const profile = profileByUser.get(userId);
      const email = String(profile?.email || (userId === access.userId ? access.email : ''));
      return {
        user_id: userId,
        full_name: String(profile?.full_name || email || `Team member ${index + 1}`),
        email,
        roles: memberships.filter((member) => String(member.user_id) === userId).map((member) => String(member.role)),
      };
    }).sort((a, b) => a.full_name.localeCompare(b.full_name));

    const nextLearnerId = nextLearners.some((learner) => learner.user_id === selectedLearnerId)
      ? selectedLearnerId
      : nextLearners.some((learner) => learner.user_id === access.userId)
        ? access.userId
        : (nextLearners[0]?.user_id || '');

    if (memberError) setError(memberError.message);
    if (profileResult.error) setError(profileResult.error.message);
    if (assignmentError) setError(assignmentError.message);
    if (!nextLearnerId && nextCourses.length > 0 && !memberError) {
      setError('No active team members are available for training.');
    }

    setCourses(nextCourses);
    setLessons(nextLessons);
    setLearners(nextLearners);
    setAssignments((assignmentRows || []) as TrainingAssignment[]);
    setSelectedLearnerId(nextLearnerId);
    setSelectedCourseId((current) => current && ids.includes(current) ? current : (ids[0] || ''));
    await loadLearnerProgress(nextLearnerId, ids);
    setLoading(false);
  }

  useEffect(() => { void load(); }, [organization.id, access.userId]);

  const selectedCourse = courses.find((course) => course.id === selectedCourseId) || courses[0];
  const selectedLearner = learners.find((learner) => learner.user_id === selectedLearnerId);
  const selectedLearnerName = selectedLearner?.full_name || (selectedLearnerId === access.userId ? access.email : 'Selected learner');
  const isViewingSelf = selectedLearnerId === access.userId;
  const selectedAssignment = assignments.find((assignment) =>
    assignment.course_id === selectedCourse?.id
    && assignment.user_id === selectedLearnerId
    && !assignment.cancelled_at
  );
  const courseLessons = useMemo(
    () => lessons.filter((lesson) => lesson.course_id === selectedCourse?.id),
    [lessons, selectedCourse?.id],
  );
  const requiredLessons = courseLessons.filter((lesson) => lesson.required);
  const referenceLessons = courseLessons.filter((lesson) => !lesson.required);
  const completionByLesson = useMemo(
    () => new Map(completions.map((item) => [item.lesson_id, item])),
    [completions],
  );

  function isComplete(lesson: Lesson) {
    const completion = completionByLesson.get(lesson.id);
    if (!completion) return false;
    if (!lesson.quiz_required) return true;
    return ['quiz', 'admin_override'].includes(completion.completion_method);
  }

  function isUnlocked(lesson: Lesson) {
    if (!selectedCourse?.sequential_required || !lesson.required) return true;
    const index = requiredLessons.findIndex((item) => item.id === lesson.id);
    if (index <= 0) return true;
    return requiredLessons.slice(0, index).every(isComplete);
  }

  const completedRequired = requiredLessons.filter(isComplete).length;
  const percent = requiredLessons.length ? Math.round((completedRequired / requiredLessons.length) * 100) : 0;
  const totalRequired = lessons.filter((lesson) => lesson.required).length;
  const totalCompleted = lessons.filter((lesson) => lesson.required && isComplete(lesson)).length;

  async function selectLearner(userId: string) {
    setError('');
    setSelectedLearnerId(userId);
    setActiveLesson(null);
    setQuizLesson(null);
    setQuizResult(null);
    await loadLearnerProgress(userId);
  }

  async function setCourseAssignment(assigned: boolean) {
    if (!supabase || !canManage || !selectedCourse || !selectedLearnerId || assignmentSaving) return;
    setAssignmentSaving(true);
    setError('');
    const now = new Date().toISOString();

    if (assigned) {
      const { error: assignmentError } = await supabase
        .from('marine_training_assignments')
        .upsert({
          organization_id: organization.id,
          course_id: selectedCourse.id,
          user_id: selectedLearnerId,
          assigned_by: access.userId,
          assigned_at: now,
          cancelled_at: null,
          cancelled_by: null,
          updated_at: now,
        }, { onConflict: 'course_id,user_id' });
      if (assignmentError) setError(assignmentError.message);
    } else {
      const { error: assignmentError } = await supabase
        .from('marine_training_assignments')
        .update({ cancelled_at: now, cancelled_by: access.userId, updated_at: now })
        .eq('organization_id', organization.id)
        .eq('course_id', selectedCourse.id)
        .eq('user_id', selectedLearnerId)
        .is('cancelled_at', null);
      if (assignmentError) setError(assignmentError.message);
    }

    await loadAssignments();
    setAssignmentSaving(false);
  }

  async function toggleManualLesson(lesson: Lesson) {
    if (!supabase || !isViewingSelf || manualSavingId || lesson.quiz_required || !isUnlocked(lesson)) return;
    setManualSavingId(lesson.id);
    setError('');
    const existing = completionByLesson.get(lesson.id);

    if (existing) {
      const { error: deleteError } = await supabase
        .from('marine_training_completions')
        .delete()
        .eq('lesson_id', lesson.id)
        .eq('user_id', access.userId);
      if (deleteError) setError(deleteError.message);
    } else {
      const { error: insertError } = await supabase.from('marine_training_completions').insert({
        organization_id: organization.id,
        course_id: lesson.course_id,
        lesson_id: lesson.id,
        user_id: access.userId,
        completion_method: 'manual',
      });
      if (insertError) setError(insertError.message);
    }

    setManualSavingId('');
    await loadLearnerProgress(access.userId);
  }

  async function openManual(course: Course) {
    if (!supabase || !course.source_storage_path) return;
    setError('');
    const { data, error: signedError } = await supabase.storage
      .from('marine-training')
      .createSignedUrl(course.source_storage_path, 60 * 60);
    if (signedError || !data?.signedUrl) {
      setError(signedError?.message || 'The training manual could not be opened.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  }

  async function uploadCourseManual(course: Course, file: File) {
    if (!supabase || !canManage || !file) return;
    if (file.type !== 'application/pdf') {
      setError('Training manuals must be uploaded as PDF files.');
      return;
    }
    setUploadingCourseId(course.id);
    setError('');
    const path = `${organization.id}/${course.id}/source-manual.pdf`;
    const upload = await supabase.storage
      .from('marine-training')
      .upload(path, file, { upsert: true, contentType: 'application/pdf' });
    if (upload.error) {
      setError(upload.error.message);
      setUploadingCourseId('');
      return;
    }
    const update = await supabase
      .from('marine_training_courses')
      .update({ source_file_name: file.name, source_storage_path: path })
      .eq('id', course.id)
      .eq('organization_id', organization.id);
    if (update.error) setError(update.error.message);
    setUploadingCourseId('');
    await load();
  }

  async function startQuiz(lesson: Lesson) {
    if (!supabase || !isViewingSelf || !lesson.quiz_required || !isUnlocked(lesson)) return;
    setQuizLesson(lesson);
    setQuizQuestions([]);
    setQuizAnswers({});
    setQuizResult(null);
    setQuizError('');
    setQuizLoading(true);

    const { data, error: questionsError } = await supabase
      .from('marine_training_quiz_questions')
      .select('id,prompt,options,sort_order')
      .eq('lesson_id', lesson.id)
      .eq('active', true)
      .order('sort_order', { ascending: true })
      .limit(lesson.quiz_question_count || 5);

    setQuizLoading(false);
    if (questionsError) {
      setQuizError(questionsError.message);
      return;
    }

    setQuizQuestions(((data || []) as Array<{id:string;prompt:string;options:unknown;sort_order:number}>).map((question) => ({
      id: question.id,
      prompt: question.prompt,
      options: Array.isArray(question.options) ? question.options.map(String) : [],
      sort_order: question.sort_order,
    })));
  }

  async function submitQuiz() {
    if (!supabase || !isViewingSelf || !quizLesson || quizSubmitting || quizQuestions.length === 0) return;
    if (quizQuestions.some((question) => quizAnswers[question.id] === undefined)) {
      setQuizError('Answer every question before submitting the test.');
      return;
    }

    setQuizSubmitting(true);
    setQuizError('');
    const { data, error: submitError } = await supabase.functions.invoke('marine-training-submit-quiz', {
      body: {
        lesson_id: quizLesson.id,
        answers: quizQuestions.map((question) => ({
          question_id: question.id,
          answer_index: quizAnswers[question.id],
        })),
      },
    });
    setQuizSubmitting(false);

    if (submitError || data?.error) {
      setQuizError(submitError?.message || String(data?.error || 'Unable to score the test.'));
      return;
    }

    setQuizResult(data as QuizResult);
    if (data?.passed) await loadLearnerProgress(access.userId);
  }

  function retakeQuiz() {
    setQuizAnswers({});
    setQuizResult(null);
    setQuizError('');
  }

  function continueToNextModule() {
    if (!quizResult?.next_lesson) {
      setQuizLesson(null);
      setQuizResult(null);
      return;
    }
    const next = lessons.find((lesson) => lesson.id === quizResult.next_lesson?.id);
    setQuizLesson(null);
    setQuizResult(null);
    if (next) setActiveLesson(next);
  }

  async function createCourse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase || !canManage) return;
    setCreating(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const title = String(form.get('title') || '').trim();
    const description = String(form.get('description') || '').trim() || null;
    const edition = String(form.get('edition') || '').trim() || null;
    const durationWeeks = Number(form.get('durationWeeks') || 0) || null;
    const sourceFile = form.get('sourceFile');
    const { data: userData } = await supabase.auth.getUser();

    const courseCode = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 64) || `course-${Date.now()}`;
    const { data: created, error: createError } = await supabase
      .from('marine_training_courses')
      .insert({
        organization_id: organization.id,
        course_code: courseCode,
        title,
        description,
        edition,
        duration_weeks: durationWeeks,
        status: 'published',
        sequential_required: true,
        created_by: userData.user?.id || null,
      })
      .select('id')
      .single();

    if (createError || !created) {
      setError(createError?.message || 'Unable to create training course.');
      setCreating(false);
      return;
    }

    if (sourceFile instanceof File && sourceFile.size > 0) {
      if (sourceFile.type !== 'application/pdf') {
        setError('The course was created, but the source manual must be a PDF.');
      } else {
        const safeName = sourceFile.name.replace(/[^a-zA-Z0-9._-]+/g, '-');
        const path = `${organization.id}/${created.id}/${safeName}`;
        const upload = await supabase.storage.from('marine-training').upload(path, sourceFile, { upsert: false, contentType: 'application/pdf' });
        if (upload.error) {
          setError(`Course created, but the PDF upload failed: ${upload.error.message}`);
        } else {
          const update = await supabase
            .from('marine_training_courses')
            .update({ source_file_name: sourceFile.name, source_storage_path: path })
            .eq('id', created.id);
          if (update.error) setError(`Course created, but the PDF could not be linked: ${update.error.message}`);
        }
      }
    }

    setCreating(false);
    setShowCreate(false);
    await load();
    setSelectedCourseId(String(created.id));
  }

  return (
    <div className="training-shell">
      <header className="module-topbar">
        <div><p>Training</p><h1>{organization.name}</h1></div>
        <div className="module-role">Learning & Professional Development</div>
      </header>

      <main className="module-content training-content">
        {error && <div className="auth-alert error">{error}</div>}

        <section className="training-hero">
          <div>
            <p>MarineOps Training</p>
            <h2>Build competence, then document it.</h2>
            <span>Tenant-specific manuals, required curriculum, reference material and individual completion records in one place.</span>
          </div>
          {canManage && <button className="platform-create-button" onClick={() => setShowCreate(true)}>+ New course</button>}
        </section>

        {!loading && learners.length > 0 && (
          <section className="training-learner-panel">
            <div className="training-learner-picker">
              <span>{canManage ? 'Viewing progress for' : 'Your training record'}</span>
              {canManage ? (
                <select
                  value={selectedLearnerId}
                  aria-label="Select course learner"
                  disabled={progressLoading}
                  onChange={(event) => void selectLearner(event.target.value)}
                >
                  {learners.map((learner) => (
                    <option value={learner.user_id} key={learner.user_id}>
                      {learner.full_name}{learner.user_id === access.userId ? ' (you)' : ''} · {learner.roles.map(humanize).join(', ')}
                    </option>
                  ))}
                </select>
              ) : (
                <strong>{selectedLearnerName}</strong>
              )}
              <small>
                {canManage
                  ? 'Choose an active team member to assign the course and review their individual record.'
                  : 'Progress and test results are saved to your signed-in account.'}
              </small>
            </div>

            {selectedCourse && (
              <div className={`training-assignment-card ${selectedAssignment ? 'assigned' : 'unassigned'}`}>
                <div>
                  <span>{selectedCourse.title}</span>
                  <strong>{selectedAssignment ? 'Course assigned' : 'Not formally assigned'}</strong>
                  <small>
                    {selectedAssignment
                      ? `Assigned ${formatShortDate(selectedAssignment.assigned_at)}`
                      : canManage
                        ? `Assign this course to ${selectedLearnerName}.`
                        : 'You can still review the available course material.'}
                  </small>
                </div>
                {canManage && (
                  <button
                    type="button"
                    className={selectedAssignment ? 'platform-row-button' : 'platform-create-button'}
                    disabled={assignmentSaving}
                    onClick={() => void setCourseAssignment(!selectedAssignment)}
                  >
                    {assignmentSaving ? 'Saving…' : selectedAssignment ? 'Unassign course' : 'Assign course'}
                  </button>
                )}
              </div>
            )}

            {!isViewingSelf && (
              <div className="training-manager-view">
                <strong>Manager view</strong>
                <span>You can review {selectedLearnerName}'s progress here. They must complete lessons and tests while signed in to their own account.</span>
              </div>
            )}
          </section>
        )}

        <section className="module-metrics">
          <TrainingMetric label="Courses" value={loading ? '—' : courses.length} detail="Published / draft programs" />
          <TrainingMetric label="Required modules" value={loading ? '—' : totalRequired} detail="Across your training library" />
          <TrainingMetric label="Passed" value={loading || progressLoading ? '—' : totalCompleted} detail={`${selectedLearnerName}'s completed required modules`} />
          <TrainingMetric label="Current progress" value={loading || progressLoading ? '—' : `${percent}%`} detail={selectedCourse?.title || 'No course selected'} />
        </section>

        {loading ? (
          <section className="platform-panel"><div className="platform-empty">Loading training library…</div></section>
        ) : courses.length === 0 ? (
          <section className="platform-panel training-empty">
            <div className="training-empty-icon">T</div>
            <h3>No training course has been added yet.</h3>
            <p>This module is ready for this organization. Add a course and optional PDF manual to begin building the tenant's training library.</p>
            {canManage && <button className="platform-create-button" onClick={() => setShowCreate(true)}>Create first course</button>}
          </section>
        ) : (
          <>
            <section className="training-course-tabs" aria-label="Training courses">
              {courses.map((course) => (
                <button key={course.id} className={selectedCourse?.id === course.id ? 'active' : ''} onClick={() => setSelectedCourseId(course.id)}>
                  <span>{course.title}</span>
                  <small>{course.edition || humanize(course.status)}</small>
                </button>
              ))}
            </section>

            {selectedCourse && (
              <section className="training-course-card">
                <div className="training-course-copy">
                  <div className="training-course-kicker">
                    <span>{selectedCourse.edition || 'Training course'}</span>
                    {selectedCourse.duration_weeks ? <span>{selectedCourse.duration_weeks}-week program</span> : null}
                    {selectedCourse.source_page_count ? <span>{selectedCourse.source_page_count} source pages</span> : null}
                    {selectedCourse.sequential_required ? <span>Sequential progression</span> : null}
                  </div>
                  <h2>{selectedCourse.title}</h2>
                  <p>{selectedCourse.description || 'Internal training and professional development.'}</p>
                  {selectedCourse.sequential_required && (
                    <div className="training-sequence-note">
                      <strong>Modules unlock in order.</strong>
                      <span>Read the current module and pass its end-of-module knowledge check before the next module opens.</span>
                    </div>
                  )}
                  {selectedCourse.completion_note && <div className="training-standard-note">{selectedCourse.completion_note}</div>}
                  <div className="training-source-row">
                    <div>
                      <small>Source manual</small>
                      <strong>{selectedCourse.source_file_name || 'No PDF attached'}</strong>
                    </div>
                    <div className="training-source-actions">
                      {selectedCourse.source_storage_path
                        ? <button className="platform-row-button" onClick={() => void openManual(selectedCourse)}>Open PDF manual</button>
                        : <span className="training-source-status">Curriculum indexed</span>}
                      {canManage && (
                        <label className="platform-row-button training-upload-button">
                          {uploadingCourseId === selectedCourse.id ? 'Uploading…' : selectedCourse.source_storage_path ? 'Replace PDF' : 'Attach PDF'}
                          <input
                            type="file"
                            accept="application/pdf"
                            disabled={uploadingCourseId === selectedCourse.id}
                            onChange={(event) => {
                              const file = event.currentTarget.files?.[0];
                              if (file) void uploadCourseManual(selectedCourse, file);
                              event.currentTarget.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                <div className="training-progress-card">
                  <div className="training-progress-ring"><strong>{percent}%</strong><span>complete</span></div>
                  <div className="training-progress-meta"><strong>{completedRequired} / {requiredLessons.length}</strong><span>required modules passed</span></div>
                  <div className="training-progress-person">{selectedLearnerName}</div>
                  <div className="training-progress-track"><span style={{ width: `${percent}%` }} /></div>
                  {completedRequired === requiredLessons.length && requiredLessons.length > 0 && (
                    <div className="training-course-complete">Course modules complete · practical approval still applies</div>
                  )}
                </div>
              </section>
            )}

            <section className="platform-panel">
              <div className="platform-panel-heading">
                <div><h3>Required curriculum</h3><p>Pass each module before the next one unlocks.</p></div>
              </div>
              <div className="training-lesson-list">
                {requiredLessons.map((lesson, index) => {
                  const done = isComplete(lesson);
                  const unlocked = isUnlocked(lesson);
                  const completion = completionByLesson.get(lesson.id);
                  const previous = index > 0 ? requiredLessons[index - 1] : null;
                  return (
                    <article className={`training-lesson ${done ? 'done' : ''} ${!unlocked ? 'locked' : ''}`} key={lesson.id}>
                      <div className={`training-state-icon ${done ? 'done' : unlocked ? 'current' : 'locked'}`}>
                        {done ? '✓' : unlocked ? String(lesson.module_number || index + 1) : '🔒'}
                      </div>
                      <div className="training-lesson-body">
                        <div className="training-lesson-title">
                          <span>{lesson.module_number ? `Module ${lesson.module_number}` : humanize(lesson.section_type)}</span>
                          <strong>{lesson.title}</strong>
                        </div>
                        {lesson.learning_objective && <p>{lesson.learning_objective}</p>}
                        {unlocked ? (
                          <div className="training-lesson-actions">
                            <button className="training-read-button" type="button" onClick={() => setActiveLesson(lesson)}>
                              {done ? 'Review module' : 'Read module'}
                            </button>
                            {lesson.quiz_required && !done && <span>{lesson.quiz_question_count}-question test · {lesson.quiz_pass_percent}% to pass</span>}
                            {done && completion?.score_percent != null && <span>Passed · {Math.round(Number(completion.score_percent))}%</span>}
                            {!lesson.quiz_required && !done && isViewingSelf && (
                              <button className="training-read-button secondary" disabled={manualSavingId === lesson.id} onClick={() => void toggleManualLesson(lesson)}>
                                {manualSavingId === lesson.id ? 'Saving…' : 'Mark complete'}
                              </button>
                            )}
                            {!isViewingSelf && <span>Manager view · learner actions are disabled</span>}
                          </div>
                        ) : (
                          <div className="training-lock-message">Complete {previous?.module_number ? `Module ${previous.module_number}` : 'the previous module'} to unlock this module.</div>
                        )}
                      </div>
                      <small className="training-page-ref">{done ? 'Passed' : unlocked ? pageLabel(lesson) : 'Locked'}</small>
                    </article>
                  );
                })}
              </div>
            </section>

            {referenceLessons.length > 0 && (
              <section className="platform-panel">
                <div className="platform-panel-heading"><div><h3>Reference library</h3><p>Supporting playbooks, checklists, assessments and professional resources remain available as reference material.</p></div></div>
                <div className="training-reference-grid">
                  {referenceLessons.map((lesson) => (
                    <article className="training-reference-card" key={lesson.id}>
                      <div className="training-reference-heading">
                        <span>{humanize(lesson.section_type)}</span>
                        <strong>{lesson.title}</strong>
                        <small>{pageLabel(lesson)}</small>
                      </div>
                      {lesson.learning_objective && <p>{lesson.learning_objective}</p>}
                      <button className="training-read-button" type="button" onClick={() => setActiveLesson(lesson)}>
                        Read section
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {activeLesson && (
        <div className="training-reader-backdrop" onMouseDown={() => setActiveLesson(null)}>
          <section className="training-reader" onMouseDown={(event) => event.stopPropagation()}>
            <header className="training-reader-header">
              <div>
                <p>{activeLesson.module_number ? `Module ${activeLesson.module_number}` : humanize(activeLesson.section_type)} · {pageLabel(activeLesson)}</p>
                <h2>{activeLesson.title}</h2>
              </div>
              <button type="button" aria-label="Close training reader" onClick={() => setActiveLesson(null)}>×</button>
            </header>
            <div className="training-reader-scroll">
              {activeLesson.learning_objective && (
                <section className="training-reader-objective">
                  <span>Learning objective</span>
                  <p>{activeLesson.learning_objective}</p>
                </section>
              )}
              <section className="training-reader-copy">
                {activeLesson.content_text
                  ? activeLesson.content_text.split('\n').map((paragraph, index) =>
                      paragraph.trim()
                        ? <p key={index}>{paragraph}</p>
                        : <div className="training-reader-spacer" key={index} />
                    )
                  : <p>No lesson text has been added yet.</p>}
              </section>
            </div>
            <footer className="training-reader-footer">
              <span>{isViewingSelf ? `${organization.name} internal training material` : `Reviewing ${selectedLearnerName}'s training record`}</span>
              <div className="training-reader-footer-actions">
                {activeLesson.required && isComplete(activeLesson) && (
                  <span className="training-passed-chip">✓ Module passed</span>
                )}
                {isViewingSelf && activeLesson.required && activeLesson.quiz_required && !isComplete(activeLesson) && isUnlocked(activeLesson) && (
                  <button className="platform-create-button" type="button" onClick={() => { setActiveLesson(null); void startQuiz(activeLesson); }}>
                    Take module test
                  </button>
                )}
                {isViewingSelf && activeLesson.required && !activeLesson.quiz_required && !isComplete(activeLesson) && isUnlocked(activeLesson) && (
                  <button className="platform-create-button" type="button" onClick={() => void toggleManualLesson(activeLesson)}>
                    Mark module complete
                  </button>
                )}
                <button className="platform-row-button" type="button" onClick={() => setActiveLesson(null)}>Close</button>
              </div>
            </footer>
          </section>
        </div>
      )}

      {quizLesson && (
        <div className="training-reader-backdrop" onMouseDown={() => !quizSubmitting && setQuizLesson(null)}>
          <section className="training-quiz-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="training-reader-header">
              <div>
                <p>Module {quizLesson.module_number} · Knowledge check</p>
                <h2>{quizLesson.title}</h2>
              </div>
              <button type="button" aria-label="Close quiz" disabled={quizSubmitting} onClick={() => setQuizLesson(null)}>×</button>
            </header>

            <div className="training-quiz-scroll">
              <div className="training-quiz-rule">
                <strong>{quizLesson.quiz_pass_percent}% required to pass.</strong>
                <span>All critical safety, ethics, client-funds and deadline questions must also be answered correctly. Attempts are recorded.</span>
              </div>

              {quizLoading ? (
                <div className="platform-empty">Loading module test…</div>
              ) : quizResult ? (
                <section className={`training-quiz-result ${quizResult.passed ? 'passed' : 'failed'}`}>
                  <div className="training-quiz-score">
                    <strong>{Math.round(quizResult.score_percent)}%</strong>
                    <span>{quizResult.correct_count} of {quizResult.total_questions} correct</span>
                  </div>
                  <div>
                    <h3>{quizResult.passed ? 'Module passed' : 'Not passed yet'}</h3>
                    <p>
                      {quizResult.passed
                        ? quizResult.course_modules_complete
                          ? 'All required modules are complete. Managing Broker assessment and delegated authority requirements still apply.'
                          : 'The next module is now unlocked.'
                        : quizResult.critical_missed > 0
                          ? `Your score was ${Math.round(quizResult.score_percent)}%, but ${quizResult.critical_missed} critical question${quizResult.critical_missed === 1 ? '' : 's'} must be corrected before this module can be completed.`
                          : `A score of at least ${quizResult.pass_percent}% is required. Review the feedback and try again.`}
                    </p>
                  </div>
                  <div className="training-feedback-list">
                    {quizQuestions.map((question, index) => {
                      const feedback = quizResult.feedback.find((item) => item.question_id === question.id);
                      if (!feedback) return null;
                      return (
                        <div className={`training-feedback-item ${feedback.correct ? 'correct' : 'incorrect'}`} key={question.id}>
                          <strong>{index + 1}. {feedback.correct ? 'Correct' : 'Review this answer'}{feedback.critical ? ' · Critical' : ''}</strong>
                          {!feedback.correct && <span>Correct answer: {question.options[feedback.correct_index]}</span>}
                          {feedback.explanation && <p>{feedback.explanation}</p>}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <div className="training-question-list">
                  {quizQuestions.map((question, index) => (
                    <fieldset className="training-question" key={question.id}>
                      <legend><span>{index + 1}</span>{question.prompt}</legend>
                      <div className="training-options">
                        {question.options.map((option, optionIndex) => (
                          <label className={quizAnswers[question.id] === optionIndex ? 'selected' : ''} key={optionIndex}>
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              checked={quizAnswers[question.id] === optionIndex}
                              onChange={() => setQuizAnswers((current) => ({ ...current, [question.id]: optionIndex }))}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ))}
                </div>
              )}

              {quizError && <div className="auth-alert error">{quizError}</div>}
            </div>

            <footer className="training-reader-footer">
              <span>{quizLesson.quiz_question_count}-question end-of-module test</span>
              <div className="training-reader-footer-actions">
                {!quizResult && !quizLoading && (
                  <button className="platform-create-button" type="button" disabled={quizSubmitting} onClick={() => void submitQuiz()}>
                    {quizSubmitting ? 'Scoring…' : 'Submit test'}
                  </button>
                )}
                {quizResult && !quizResult.passed && (
                  <button className="platform-create-button" type="button" onClick={retakeQuiz}>Retake test</button>
                )}
                {quizResult?.passed && (
                  <button className="platform-create-button" type="button" onClick={continueToNextModule}>
                    {quizResult.course_modules_complete ? 'Return to course' : 'Continue to next module'}
                  </button>
                )}
                <button className="platform-row-button" type="button" disabled={quizSubmitting} onClick={() => setQuizLesson(null)}>Close</button>
              </div>
            </footer>
          </section>
        </div>
      )}

      {showCreate && (
        <div className="platform-modal-backdrop" onMouseDown={() => !creating && setShowCreate(false)}>
          <section className="platform-modal wide" onMouseDown={(event) => event.stopPropagation()}>
            <div className="platform-modal-heading"><div><p>MarineOps Training</p><h2>New training course</h2></div><button type="button" onClick={() => setShowCreate(false)}>×</button></div>
            <form className="platform-modal-form" onSubmit={createCourse}>
              <label>Course title<input name="title" required placeholder="Broker Academy"/></label>
              <label>Description<textarea name="description" rows={4} placeholder="What this program trains and who it is for."/></label>
              <label>Edition / version<input name="edition" placeholder="Edition 1.0"/></label>
              <label>Duration in weeks<input name="durationWeeks" type="number" min="1" max="104"/></label>
              <label>Source manual (PDF)<input name="sourceFile" type="file" accept="application/pdf"/></label>
              <div className="platform-modal-actions">
                <button type="button" className="platform-row-button" onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="platform-create-button" disabled={creating}>{creating ? 'Creating…' : 'Create course'}</button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

function TrainingMetric({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return <div className="platform-metric"><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>;
}

function pageLabel(lesson: Lesson) {
  if (!lesson.source_page_start) return '';
  if (!lesson.source_page_end || lesson.source_page_end === lesson.source_page_start) return `Page ${lesson.source_page_start}`;
  return `Pages ${lesson.source_page_start}–${lesson.source_page_end}`;
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
}

function humanize(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
